import React, { useState, useEffect } from 'react';
import { Compass, Flame, BookOpen, Type } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { dbManager } from '../services/db_manager';
import { uniqueWordsService } from '../services/uniqueWordsService';

const STATS_I18N: Record<string, any> = {
  pt: {
    levelPrefix: 'Nível',
    topicsSuffix: 'Tópicos',
    daysSuffix: 'Dias',
    booksSuffix: 'Livros',
    wordsSuffix: 'Palavras'
  },
  en: {
    levelPrefix: 'Level',
    topicsSuffix: 'Topics',
    daysSuffix: 'Days',
    booksSuffix: 'Books',
    wordsSuffix: 'Words'
  },
  es: {
    levelPrefix: 'Nivel',
    topicsSuffix: 'Temas',
    daysSuffix: 'Días',
    booksSuffix: 'Libros',
    wordsSuffix: 'Palabras'
  },
  fr: {
    levelPrefix: 'Niveau',
    topicsSuffix: 'Sujets',
    daysSuffix: 'Jours',
    booksSuffix: 'Livres',
    wordsSuffix: 'Mots'
  },
  de: {
    levelPrefix: 'Stufe',
    topicsSuffix: 'Themen',
    daysSuffix: 'Tage',
    booksSuffix: 'Bücher',
    wordsSuffix: 'Wörter'
  },
  it: {
    levelPrefix: 'Livello',
    topicsSuffix: 'Argomenti',
    daysSuffix: 'Giorni',
    booksSuffix: 'Libri',
    wordsSuffix: 'Parole'
  },
  hu: {
    levelPrefix: 'Szint',
    topicsSuffix: 'Témák',
    daysSuffix: 'Nap',
    booksSuffix: 'Könyvek',
    wordsSuffix: 'Szavak'
  },
  ru: {
    levelPrefix: 'Уровень',
    topicsSuffix: 'Темы',
    daysSuffix: 'Дней',
    booksSuffix: 'Книг',
    wordsSuffix: 'Слов'
  },
  zh: {
    levelPrefix: '等级',
    topicsSuffix: '主题',
    daysSuffix: '天',
    booksSuffix: '图书',
    wordsSuffix: '单词'
  },
  ja: {
    levelPrefix: 'レベル',
    topicsSuffix: 'トピック',
    daysSuffix: '日',
    booksSuffix: '本',
    wordsSuffix: '単語'
  },
  nl: {
    levelPrefix: 'Niveau',
    topicsSuffix: 'Onderwerpen',
    daysSuffix: 'Dagen',
    booksSuffix: 'Boeken',
    wordsSuffix: 'Woorden'
  }
};

export const TopStatsBar: React.FC<{ activeLangOverride?: string }> = ({ activeLangOverride }) => {
  // TopStatsBar MUST appear SOMENTE nas URLs '/roadmap'
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/roadmap')) {
    return null;
  }

  const { user } = useAuth();
  const { profile, books } = useData();
  const { targetLanguage } = useTheme();
  const { i18n } = useTranslation();

  const cleanNative = (i18n.language || profile?.native_language || 'pt').split('-')[0].toLowerCase();
  const tLabels = STATS_I18N[cleanNative] || STATS_I18N['pt'];
  const targetLangCode = (activeLangOverride || targetLanguage || 'en').toLowerCase();

  const [roadmapStats, setRoadmapStats] = useState<{ level: string; completedTopics: number; totalTopics: number }>({
    level: 'A1',
    completedTopics: 0,
    totalTopics: 31
  });
  const [booksCount, setBooksCount] = useState<number>(books?.length || 0);
  const [uniqueWordsCount, setUniqueWordsCount] = useState<number>(0);

  useEffect(() => {
    if (books && Array.isArray(books)) {
      const langBooks = books.filter((b: any) => {
        if (!b.language) return false;
        const bLang = b.language.split('-')[0].toLowerCase();
        return bLang === targetLangCode;
      });
      setBooksCount(langBooks.length);
    }
  }, [books, targetLangCode]);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      if (!user?.id) return;

      try {
        // 1. Fetch roadmap progress from Cloudflare D1
        const roadmapResults = await dbManager.from('roadmap_progress', user.id).select({
          user_id: user.id
        });

        let localCompletedCount = 0;
        try {
          const saved = localStorage.getItem('libritui_roadmap_completed_topics');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) {
              localCompletedCount = parsed.length;
            }
          }
        } catch {}

        if (isMounted && roadmapResults && Array.isArray(roadmapResults) && roadmapResults.length > 0) {
          const matched = roadmapResults.find(
            (r: any) => r.language_code && r.language_code.toLowerCase() === targetLangCode
          ) || roadmapResults[0];

          if (matched) {
            const d1CompletedCount = matched.current_topic_index || 0;
            const finalCompletedTopics = Math.max(d1CompletedCount, localCompletedCount);

            setRoadmapStats({
              level: (matched.current_level || 'A1').toUpperCase(),
              completedTopics: finalCompletedTopics,
              totalTopics: 31
            });
          }
        } else if (isMounted && localCompletedCount > 0) {
          setRoadmapStats(prev => ({
            ...prev,
            completedTopics: localCompletedCount
          }));
        }

        // 2. Fetch books count from D1 filtered by target language
        const booksResults = await dbManager.from('books', user.id).select({
          user_id: user.id
        });

        if (isMounted && booksResults && Array.isArray(booksResults)) {
          const langBooks = booksResults.filter((b: any) => {
            if (!b.language) return false;
            const bLang = b.language.split('-')[0].toLowerCase();
            return bLang === targetLangCode;
          });
          setBooksCount(langBooks.length);
        }

        // 3. Fetch unique words count from D1 logs_unique_words table via uniqueWordsService
        const wordsCount = await uniqueWordsService.getUniqueWordsCount(user.id, targetLangCode);
        if (isMounted) {
          setUniqueWordsCount(wordsCount);
        }

      } catch (err) {
        console.error('[TopStatsBar] Erro ao carregar dados do D1:', err);
      }
    };

    fetchStats();

    const handleUniqueWordsUpdate = (e: any) => {
      if (e.detail?.language === targetLangCode || !e.detail?.language) {
        setUniqueWordsCount(e.detail?.total || 0);
      }
    };

    window.addEventListener('libritui-unique-words-updated', handleUniqueWordsUpdate);

    return () => {
      isMounted = false;
      window.removeEventListener('libritui-unique-words-updated', handleUniqueWordsUpdate);
    };
  }, [user?.id, targetLangCode]);

  const streakDays = profile?.current_streak || profile?.streak || 0;

  return (
    <div className="w-full bg-surface/90 backdrop-blur-md border border-black/[0.06] dark:border-white/[0.06] rounded-2xl px-2 py-3 transition-all duration-300">
      <div className="grid grid-cols-4 divide-x divide-black/[0.06] dark:divide-white/[0.06]">
        {[
          { icon: <Compass size={14} strokeWidth={1.75} />, value: `${roadmapStats.completedTopics}/${roadmapStats.totalTopics}`, label: `${roadmapStats.level} · ${tLabels.topicsSuffix.toLowerCase()}` },
          { icon: <Flame size={14} strokeWidth={1.75} />, value: String(streakDays), label: tLabels.daysSuffix.toLowerCase() },
          { icon: <BookOpen size={14} strokeWidth={1.75} />, value: String(booksCount), label: tLabels.booksSuffix.toLowerCase() },
          { icon: <Type size={14} strokeWidth={1.75} />, value: String(uniqueWordsCount), label: tLabels.wordsSuffix.toLowerCase() },
        ].map((stat, i) => (
          <div key={i} className="flex flex-col items-center gap-1 px-1 min-w-0">
            <span className="text-primary">{stat.icon}</span>
            <span className="font-display text-lg leading-none text-ink tabular-nums">{stat.value}</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted truncate max-w-full">{stat.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TopStatsBar;
